import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection } from '../types.js';
import {
  bilingual,
  description,
  ARABIC_LINE,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionSubtitle,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Three panels: a featured item drawn large under the brand, a bold accent column of specials,
 * and a list with round photos. Sections flow into the two right-hand panels in order.
 */

/** The starred item, or failing that the first one with a photo. */
export function featuredItem(board: MenuBoardContent): MenuItemContent | null {
  const starred = board.featuredItemId && board.items.find((i) => i.id === board.featuredItemId);
  return starred || board.items.find((i) => i.imageMediaId) || null;
}

function itemPrice(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  if (!item.variants.length) return sellingPrice(ctx, item, cls);
  return item.variants
    .map((v) => `<span class="${cls}"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('');
}

function hero(ctx: RenderContext): string {
  const { board } = ctx;
  const brand = board.brand;
  const logo = image(ctx, brand.logoMediaId, 'cp-logo');
  const brandName = bilingual(ctx, brand.name, brand.nameAr, 'cp-brand');
  const tagline = bilingual(ctx, brand.tagline, brand.taglineAr, 'cp-tagline');
  const top = logo || brandName || tagline ? `<div class="cp-brandblock">${logo}${brandName}${tagline}</div>` : '';

  const item = featuredItem(board);
  if (!item) {
    const title = bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'cp-hero-caps');
    return `${top}<div class="cp-hero-text">${title}</div>`;
  }

  // "Caramel Latte" reads as a script "Caramel" over a capital "LATTE".
  const words = item.title.trim().split(/\s+/);
  const scriptPart = words.length > 1 ? words.slice(0, -1).join(' ') : null;
  const capsPart = words[words.length - 1] ?? item.title;
  const english = ctx.showEn
    ? (scriptPart ? `<p class="cp-hero-script">${escapeHtml(scriptPart)}</p>` : '') +
      `<p class="cp-hero-caps">${escapeHtml(capsPart)}</p>`
    : '';
  const arabic =
    ctx.showAr && item.titleAr?.trim()
      ? `<p class="cp-hero-caps mb-ar${ctx.showEn ? ' mb-2nd' : ''}" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  // A long name shrinks rather than running off the panel.
  const scale = Math.min(fitScale(capsPart, 9), fitScale(scriptPart, 12), fitScale(item.titleAr, 16));
  return [
    image(ctx, item.imageMediaId, 'cp-hero-img'),
    top,
    '<div class="cp-hero-text">',
    `<div style="font-size:${scale.toFixed(3)}em">`,
    english || arabic ? english + arabic : `<p class="cp-hero-caps">${escapeHtml(item.title)}</p>`,
    '</div>',
    description(ctx, item, 'cp-hero-desc'),
    `<div class="cp-hero-price">${itemPrice(ctx, item, 'cp-hp')}</div>`,
    '</div>',
  ].join('');
}

function specials(ctx: RenderContext, block: MenuBlock): string {
  const head =
    hasTitle(block.section) && !block.continued
      ? `<div class="cp-sp-head"><div style="font-size:${specialsTitleScale(block.section).toFixed(3)}em">${sectionTitle(ctx, block.section, 'cp-sp-title')}</div>${sectionSubtitle(ctx, block.section, 'cp-sp-sub')}</div>`
      : '';
  const items = block.items
    .map(
      (item) =>
        `<div class="cp-sp-item">${bilingual(ctx, item.title, item.titleAr, 'cp-sp-name')}${description(ctx, item, 'cp-sp-desc')}<div class="cp-sp-price">${itemPrice(ctx, item, 'cp-sp-p')}</div></div>`,
    )
    .join('<hr class="cp-sep">');
  return `<div class="cp-sp-block">${head}${items}</div>`;
}

function list(ctx: RenderContext, block: MenuBlock): string {
  const head = hasTitle(block.section)
    ? `<div class="cp-ls-head">${sectionTitle(ctx, block.section, 'cp-ls-title')}</div>${block.continued ? '' : sectionSubtitle(ctx, block.section, 'cp-ls-sub')}`
    : '';
  const rows = block.items
    .map((item) => {
      const photo = image(ctx, item.imageMediaId, 'cp-ls-img');
      return [
        '<div class="cp-ls-row">',
        photo ? `<div class="cp-ls-ph">${photo}</div>` : '<div class="cp-ls-ph cp-ls-ph-empty"></div>',
        '<div class="cp-ls-text">',
        bilingual(ctx, item.title, item.titleAr, 'cp-ls-name'),
        description(ctx, item, 'cp-ls-desc'),
        `<div class="cp-ls-price">${itemPrice(ctx, item, 'cp-ls-p')}</div>`,
        '</div></div>',
      ].join('');
    })
    .join('');
  return `<div class="cp-ls-block">${head}${rows}</div>`;
}

/** The specials heading is set huge; about ten capitals fit across the column. */
function specialsTitleScale(section: MenuSection): number {
  return Math.min(fitScale(section.title, 10), fitScale(section.titleAr, 13));
}

/**
 * Heights in em at the reference size, line by line, so a bilingual board (which stacks an
 * Arabic line under each English one) is costed for what it really draws.
 */
function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const desc = board.settings.showDescriptions;
  const capacity = portrait ? 60 : 61;
  // English is drawn when asked for, and as the fallback for a missing translation.
  const names = (item: MenuItemContent, size: number, arSize: number) =>
    (en || !item.titleAr ? size : 0) + (ar && item.titleAr ? (en ? arSize : size) * ARABIC_LINE : 0);
  const descLines = (item: MenuItemContent, perLine: number) =>
    desc
      ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
        (ar ? lineCount(item.descriptionAr, perLine, 2) : 0)
      : 0;

  const specialsColumn: MenuColumn = {
    capacity,
    blockCost: (section, items, continued) => {
      let head = 0;
      if (hasTitle(section) && !continued) {
        head += (en && section.title ? 5 : 0) * specialsTitleScale(section);
        head += ar && section.titleAr ? (en ? 3.1 : 5 * specialsTitleScale(section)) * ARABIC_LINE : 0;
        // A missing translation falls back to the English line, which still takes room.
        head += (en || !section.subtitleAr) && section.subtitle ? 3.7 : 0;
        head += ar && section.subtitleAr ? 2.6 * ARABIC_LINE : 0;
        head += 1.2;
      }
      const body = items.reduce(
        (sum, item) => sum + names(item, 2, 1.55) + descLines(item, portrait ? 34 : 50) * 1.3 + 2 + 2.3,
        0,
      );
      return head + body + 2;
    },
  };
  const listColumn: MenuColumn = {
    capacity,
    blockCost: (section, items) => {
      const head = hasTitle(section) ? 2.6 + (en && section.subtitle ? 1.8 : 0) + 1 : 0;
      const body = items.reduce((sum, item) => {
        const text = names(item, 1.8, 1.45) + descLines(item, portrait ? 22 : 32) * 1.27 + 1.8;
        return sum + Math.max(portrait ? 7 : 8.5, text) + 1.5;
      }, 0);
      return head + body;
    },
  };
  return [specialsColumn, listColumn];
}

export const cafePanels: MenuTemplate = {
  id: 'cafe-panels',
  name: 'Café panels',
  description:
    'Your featured item large beside the brand, a bold column of specials, and a list with round photos.',
  defaultTheme: {
    backgroundColor: '#182335',
    surfaceColor: '#1d2a40',
    textColor: '#ffffff',
    primaryColor: '#ffffff',
    accentColor: '#e8742c',
    fontId: 'cafe',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'navy', name: 'Navy & orange', theme: {} },
    { id: 'espresso', name: 'Espresso', theme: { backgroundColor: '#2b1d16', surfaceColor: '#34241b', accentColor: '#c8935a' } },
    { id: 'sage', name: 'Sage', theme: { backgroundColor: '#1e2b25', surfaceColor: '#24342c', accentColor: '#d08c45' } },
    { id: 'noir', name: 'Noir & red', theme: { backgroundColor: '#121212', surfaceColor: '#1b1b1b', accentColor: '#d62839', fontId: 'bold' } },
  ],
  cuisines: ['cafe'],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const [middle = [], right = []] = ctx.page.columns;
    return [
      `<div class="cp-hero">${hero(ctx)}</div>`,
      `<div class="cp-specials">${middle.map((b) => specials(ctx, b)).join('')}</div>`,
      `<div class="cp-list">${right.map((b) => list(ctx, b)).join('')}</div>`,
    ].join('');
  },

  css: `
.mb-t-cafe-panels{display:grid;grid-template-columns:36fr 27fr 37fr;padding:0}
.mb-t-cafe-panels.mb-portrait{grid-template-columns:1fr 1fr;grid-template-rows:42fr 58fr}
.mb-t-cafe-panels.mb-portrait .cp-hero{grid-column:1 / span 2}
.mb-t-cafe-panels>div{position:relative;min-height:0;min-width:0;overflow:hidden}
.mb-t-cafe-panels .cp-hero{padding:3em;display:flex;flex-direction:column;background:var(--mb-bg)}
.mb-t-cafe-panels .cp-brandblock{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;text-align:center;gap:.5em}
.mb-t-cafe-panels .cp-logo{height:5.5em;max-width:60%;object-fit:contain}
.mb-t-cafe-panels .cp-brand{font-family:var(--mb-font-heading);font-weight:500;font-size:2.6em;letter-spacing:.08em;
  text-transform:uppercase;color:var(--mb-primary);line-height:1.05}
.mb-t-cafe-panels .cp-tagline{display:flex;align-items:center;gap:.7em;color:var(--mb-accent);font-size:1.05em;
  letter-spacing:.3em;text-transform:uppercase;font-weight:600}
.mb-t-cafe-panels .cp-tagline:before,.mb-t-cafe-panels .cp-tagline:after{content:'';width:2em;height:.1em;background:currentColor}
.mb-t-cafe-panels .cp-hero-img{position:absolute;left:0;right:0;bottom:0;width:100%;height:74%;object-fit:cover;z-index:0;
  -webkit-mask-image:linear-gradient(to bottom,transparent,#000 28%);mask-image:linear-gradient(to bottom,transparent,#000 28%)}
.mb-t-cafe-panels .cp-hero:after{content:'';position:absolute;inset:auto 0 0 0;height:55%;z-index:1;
  background:linear-gradient(to top,color-mix(in srgb,var(--mb-bg) 92%,transparent),transparent)}
.mb-t-cafe-panels .cp-hero-text{position:relative;z-index:2;margin-top:auto;max-width:62%;text-shadow:0 .1em .6em rgba(0,0,0,.45)}
.mb-t-cafe-panels.mb-portrait .cp-hero-text{max-width:50%}
.mb-t-cafe-panels .cp-hero-script{font-family:var(--mb-font-script);font-size:4.2em;color:var(--mb-accent);line-height:1}
.mb-t-cafe-panels .cp-hero-caps{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:5em;
  text-transform:uppercase;color:var(--mb-primary);line-height:.95;letter-spacing:.02em}
.mb-t-cafe-panels .cp-hero-caps.mb-2nd{font-size:2.6em;margin-top:.15em}
.mb-t-cafe-panels .cp-hero-desc{font-size:1.15em;opacity:.92;margin-top:.6em;max-width:22em}
.mb-t-cafe-panels .cp-hero-price{display:flex;flex-wrap:wrap;gap:.1em 1em;align-items:baseline;margin-top:.5em}
.mb-t-cafe-panels .cp-hp{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.6em;color:var(--mb-accent)}
.mb-t-cafe-panels .cp-hp-old{font-size:1.4em;opacity:.6}
.mb-t-cafe-panels .cp-hp small,.mb-t-cafe-panels .cp-sp-p small,.mb-t-cafe-panels .cp-ls-p small{font-size:.55em;opacity:.85;margin-inline-end:.2em}
.mb-t-cafe-panels .cp-specials{background:var(--mb-accent);color:var(--mb-text);padding:3em 2.4em;text-align:center;
  display:flex;flex-direction:column;gap:2em;
  background-image:radial-gradient(circle at 15% 8%,rgba(255,255,255,.16),transparent 42%),radial-gradient(circle at 85% 95%,rgba(0,0,0,.16),transparent 48%)}
.mb-t-cafe-panels .cp-sp-head{margin-bottom:1.2em}
.mb-t-cafe-panels .cp-sp-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:5.2em;
  text-transform:uppercase;line-height:.95;color:var(--mb-text)}
.mb-t-cafe-panels .cp-sp-title.mb-2nd{font-size:2.8em}
.mb-t-cafe-panels .cp-sp-sub{font-family:var(--mb-font-script);font-size:3.4em;color:var(--mb-bg);transform:rotate(-5deg);
  margin-top:-.3em;line-height:1.1}
.mb-t-cafe-panels .cp-sp-sub.mb-2nd{font-size:2.2em;transform:none;margin-top:.1em}
.mb-t-cafe-panels .cp-sp-head div:has(.mb-ar)+.cp-sp-sub{margin-top:.15em}
.mb-t-cafe-panels .cp-sp-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;
  text-transform:uppercase;line-height:1.05}
.mb-t-cafe-panels .cp-sp-name.mb-2nd{font-size:1.4em}
.mb-t-cafe-panels .cp-sp-desc{font-size:1em;opacity:.92;margin:.3em auto 0;max-width:20em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mb-t-cafe-panels .cp-sp-price{display:flex;flex-wrap:wrap;justify-content:center;gap:.1em .8em;margin-top:.3em}
.mb-t-cafe-panels .cp-sp-p{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.8em}
.mb-t-cafe-panels .cp-sp-p-old{font-size:1.1em;opacity:.7}
.mb-t-cafe-panels .cp-sep{border:0;border-top:.1em solid rgba(255,255,255,.5);width:55%;margin:1.1em auto}
.mb-t-cafe-panels .cp-list{background:var(--mb-surface);padding:3em;display:flex;flex-direction:column;gap:1.5em}
.mb-t-cafe-panels .cp-ls-head{display:flex;align-items:center;gap:.8em;justify-content:center}
.mb-t-cafe-panels .cp-ls-head:before,.mb-t-cafe-panels .cp-ls-head:after{content:'';flex:0 1 3em;height:.12em;background:var(--mb-accent)}
.mb-t-cafe-panels .cp-ls-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;
  text-transform:uppercase;letter-spacing:.06em;color:var(--mb-accent);text-align:center}
.mb-t-cafe-panels .cp-ls-title.mb-2nd{font-size:1.5em}
.mb-t-cafe-panels .cp-ls-sub{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.5em;text-align:center}
.mb-t-cafe-panels .cp-ls-row{display:grid;grid-template-columns:8.5em 1fr;gap:1.6em;align-items:center;margin-top:1.5em}
.mb-t-cafe-panels.mb-portrait .cp-ls-row{grid-template-columns:7em 1fr;gap:1.2em}
.mb-t-cafe-panels .cp-ls-ph{width:100%;aspect-ratio:1;border-radius:50%;overflow:hidden;
  box-shadow:0 0 0 .35em rgba(255,255,255,.1),0 .6em 1.4em rgba(0,0,0,.35)}
.mb-t-cafe-panels .cp-ls-ph-empty{background:rgba(255,255,255,.06)}
.mb-t-cafe-panels .cp-ls-img{width:100%;height:100%;object-fit:cover;display:block}
.mb-t-cafe-panels .cp-ls-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.7em;
  text-transform:uppercase;color:var(--mb-primary);line-height:1.05}
.mb-t-cafe-panels .cp-ls-name.mb-2nd{font-size:1.3em}
.mb-t-cafe-panels .cp-ls-desc{font-size:.98em;opacity:.85;margin-top:.25em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mb-t-cafe-panels .cp-ls-price{display:flex;justify-content:flex-end;flex-wrap:wrap;gap:.1em .8em;align-items:baseline}
.mb-t-cafe-panels .cp-ls-p{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;color:var(--mb-accent)}
.mb-t-cafe-panels .cp-ls-p-old{font-size:1em;opacity:.6}
`,
};
